import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';

import LegacyRequest from '../../util/Api';

export type ApiError = {
    message: string;
    fields?: Record<string, string>;
    // Surfaced by endpoints that refuse a destructive op with a
    // structured live-reference list (currently the per-card delete
    // guardrail). Forward whatever the backend included verbatim.
    violations?: Array<{ label: string; count: number }>;
};

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type BaseQueryArgs = {
    url: string;
    method: Method;
    query?: Record<string, unknown>;
    params?: Record<string, unknown>;
    body?: unknown;
    /**
     * Extra axios options forwarded to util/Api — e.g. `{ responseType:
     * 'blob' }` for binary downloads. The legacy wrapper only spreads
     * these on GET and POST.
     */
    opts?: Record<string, unknown>;
};

type RequestShape = {
    get: (url: string, query?: unknown, params?: unknown, opts?: unknown) => Promise<any>;
    post: (url: string, query: unknown, params: unknown, body: unknown, opts?: unknown) => Promise<any>;
    put: (url: string, query: unknown, params: unknown, body: unknown) => Promise<any>;
    patch: (url: string, query: unknown, params: unknown, body: unknown) => Promise<any>;
    delete: (url: string, query?: unknown, params?: unknown) => Promise<any>;
};

const Request = LegacyRequest as unknown as RequestShape;

const baseQuery: BaseQueryFn<BaseQueryArgs, unknown, ApiError> = async ({
    url,
    method,
    query,
    params,
    body,
    opts,
}) => {
    try {
        let data: unknown;
        switch (method) {
            case 'GET':
                data = await Request.get(url, query, params, opts);
                break;
            case 'DELETE':
                data = await Request.delete(url, query, params);
                break;
            case 'POST':
                data = await Request.post(url, query, params, body, opts);
                break;
            case 'PUT':
                data = await Request.put(url, query, params, body);
                break;
            case 'PATCH':
                data = await Request.patch(url, query, params, body);
                break;
        }
        return { data };
    } catch (e) {
        const err = (e ?? { message: 'unknown' }) as ApiError;
        return {
            error: {
                message: err.message ?? 'unknown',
                fields: err.fields,
                violations: err.violations,
            },
        };
    }
};

export const api = createApi({
    reducerPath: 'v2Api',
    baseQuery,
    tagTypes: [
        'Product',
        'Job',
        'Category',
        'Supplier',
        'Unit',
        'User',
        'Role',
        'UserCurrency',
        'UserCurrencyLog',
        'UserTag',
        'UserTagAssignment',
        'Tag',
        'Impersonation',
        'PriceList',
        'PriceListItem',
        'ProductAccessRule',
        'CardGame',
        'CardExpansion',
        'SinglesCard',
        'PriceAnomaly',
        'DayReport',
        'Thread',
        'ThreadList',
        'OrdersWithIssues',
        'OrderFlag',
        'OrderFlagTag',
        'OrderFlagActive',
        'ShortUrl',
        'PushMe',
        'PushRecipient',
        'PushNotification',
        'Giveaway',
        'GiveawayRedemption',
        'GiveawayCurrencyLog',
        'PosLoyaltyItems',
        'AnalyticsEvent',
        'CalendarEvent',
        'CalendarEventConsumables',
        'EventReservation',
        'HrShop',
        'HrEmployee',
        'HrVacation',
        'Ticket',
        'TicketEvent',
        'TicketAttachment',
        'TicketReference',
        'TicketLabelColor',
    ],
    endpoints: () => ({}),
});
